import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";

import { useGetDomain } from "@/hooks/useDomain";
import { DEPLOY_BASE_DOMAIN, FALLBACK_ERROR_MESSAGE } from "@/lib/constants";
import { addDomain, reDeploy } from "@/lib/deploy/api";
import { DOMAIN_STATUS } from "@/lib/enums";
import { humanizeString } from "@/lib/utils";
import {
  DomainFormData,
  domainSchema,
  UpdateDeploySettingsFormData,
} from "@/lib/validations";
import { Deploy } from "@/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  CheckCircle,
  Clock,
  Copy,
  ExternalLink,
  Globe,
  HelpCircle,
  Loader2,
  Plus,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import {
  default as LoadingSpinner,
  default as Spinner,
} from "../Common/Spinner";

interface CustomDomainsDialogProps {
  deploy: Deploy;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}
export interface AddDomainInput extends DomainFormData {
  deployId: string;
}

export function CustomDomainsDialog({
  deploy,
  open,
  onOpenChange,
}: CustomDomainsDialogProps) {
  const { toast } = useToast();
  const [domainRemoveLoading, setDomainRemoveLoading] = useState(false);
  const domainQuery = useGetDomain(deploy.customDomainId);
  const getDeployUrl = () => {
    return !domainQuery.isLoading &&
      domainQuery.data?.verified &&
      deploy.customDomain
      ? deploy.customDomain
      : deploy.subDomain;
  };

  const queryClient = useQueryClient();
  const addDomainMutation = useMutation({
    mutationFn: addDomain,
    onSuccess: (data) => {
      queryClient.setQueryData(["deploys"], (old: any) => [
        data,
        ...(old || []),
      ]);
      queryClient.invalidateQueries({ queryKey: ["deploys"] });
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.response?.data?.message ?? FALLBACK_ERROR_MESSAGE,
        variant: "destructive",
      });
    },
  });
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<DomainFormData>({
    resolver: zodResolver(domainSchema),
  });
  const updateDeployMutation = useMutation({
    mutationFn: reDeploy,
    onSuccess: (data) => {
      queryClient.setQueryData(["deploys"], (old: any) => [
        data,
        ...(old || []),
      ]);
      queryClient.invalidateQueries({ queryKey: ["deploys"] });
      toast({
        title: "Domain removal queued.",
        description: "Your domain will be removed in a few minutes.",
      });
      setDomainRemoveLoading(false);
      onOpenChange(false);
    },
    onError: (error: any) => {
      setDomainRemoveLoading(false);

      toast({
        title: "Error",
        description: error?.response?.data?.message ?? FALLBACK_ERROR_MESSAGE,
        variant: "destructive",
      });
    },
  });
  const onSubmit = async (data: DomainFormData) => {
    if (data.domain.includes(DEPLOY_BASE_DOMAIN)) {
      toast({
        title: "Error",
        description: "Invalid custom domain",
        variant: "destructive",
      });
    }
    addDomainMutation.mutate(
      {
        ...data,
        deployId: deploy.id,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["deploys"] });
          toast({
            title: "Domain added successfully!",
            description: `${data.domain} has been added. Please configure the DNS records to activate it.`,
          });
          reset();
        },
      },
    );
  };

  const removeDomain = async () => {
    setDomainRemoveLoading(true);
    const redeploy = {
      id: deploy.id,
      customDomainDelete: true,
    } as UpdateDeploySettingsFormData;

    updateDeployMutation.mutate(redeploy);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied!",
      description: "DNS record copied to clipboard.",
    });
  };

  const getSSLStatusIcon = (status: string) => {
    switch (status) {
      case "active":
        return <CheckCircle className="h-4 w-4 text-green-400" />;
      case "pending":
        return <Clock className="h-4 w-4 text-yellow-400 animate-pulse" />;
      case "failed":
        return <AlertCircle className="h-4 w-4 text-red-400" />;
      default:
        return <Clock className="h-4 w-4 text-slate-400" />;
    }
  };

  const getSSLStatusBadge = (status: string) => {
    const colors = {
      active: "text-green-400 bg-green-400/10 border-green-400/30",
      pending: "text-yellow-400 bg-yellow-400/10 border-yellow-400/30",
      error: "text-red-400 bg-red-400/10 border-red-400/30",
    };

    return (
      <Badge
        className={`${
          colors[status as keyof typeof colors]
        }  flex items-center gap-1`}
      >
        {getSSLStatusIcon(status)}
        {humanizeString(status)}
      </Badge>
    );
  };

  const getDNSStatusBadge = (configured: boolean) => {
    return (
      <Badge
        className={`${
          configured
            ? "text-green-400 bg-green-400/10 border-green-400/30"
            : "text-orange-400 bg-orange-400/10 border-orange-400/30"
        }  flex items-center gap-1`}
      >
        {configured ? (
          <CheckCircle className="h-3 w-3" />
        ) : (
          <Clock className="h-3 w-3 animate-pulse" />
        )}
        {configured ? "VERIFIED" : "PENDING"}
      </Badge>
    );
  };

  if (domainQuery.isLoading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-screen overflow-y-auto bg-slate-900 border border-cyan-500/30">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-cyan-400 ">
              <Globe className="h-5 w-5" />
              CUSTOM DOMAIN
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto space-y-6">
            {/* Current Default URL */}
            <Card className="holo-card border-blue-500/30">
              <LoadingSpinner></LoadingSpinner>
            </Card>
          </div>
        </DialogContent>
      </Dialog>
    );
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-screen overflow-y-auto bg-slate-900 border border-cyan-500/30">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-cyan-400 ">
            <Globe className="h-5 w-5" />
            CUSTOM DOMAIN
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto space-y-6">
          {/* Current Default URL */}
          <Card className="holo-card border-blue-500/30">
            <CardHeader>
              <CardTitle className="text-blue-400  flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Current App URL
              </CardTitle>
              <CardDescription className="text-slate-400 ">
                Your app is currently accessible at this URL
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 bg-slate-800/30 border border-blue-500/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <code className="text-blue-300  bg-slate-900/50 px-3 py-2 rounded">
                    https://{getDeployUrl()}
                  </code>
                  <Badge className="text-green-400 bg-green-400/10 border-green-400/30 ">
                    ACTIVE
                  </Badge>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(`https://${getDeployUrl()}`)}
                    className="text-slate-400 hover:text-cyan-400"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      window.open(
                        `https://${getDeployUrl()}`,
                        "_blank",
                        "noopener,noreferrer",
                      )
                    }
                    className="text-slate-400 hover:text-cyan-400"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Add Domain */}
          {!deploy.customDomain && (
            <Card className="holo-card">
              <CardHeader>
                <CardTitle className="text-cyan-400 ">
                  Add Custom Domain
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="domain" className="text-cyan-300 ">
                      Domain Name
                    </Label>
                    <div className="flex gap-4">
                      <div className="flex-1">
                        <Input
                          id="domain"
                          {...register("domain")}
                          placeholder="myapp.com or app.mydomain.com"
                          className="bg-slate-800/50 border-cyan-500/30 text-cyan-100  focus:border-cyan-400"
                        />
                        {errors.domain && (
                          <p className="text-red-400 text-xs  mt-1">
                            {errors.domain.message}
                          </p>
                        )}
                      </div>
                      <Button
                        type="submit"
                        disabled={addDomainMutation.isPending}
                        className="cyber-button "
                      >
                        {addDomainMutation.isPending ? (
                          <Loader2 className="h-5 w-5 mr-3 animate-spin" />
                        ) : (
                          <Plus className="h-4 w-4 mr-2" />
                        )}
                        {addDomainMutation.isPending
                          ? "ADDING..."
                          : "ADD DOMAIN"}
                      </Button>
                    </div>
                  </div>

                  <Alert className="border-blue-500/30 bg-blue-500/10">
                    <HelpCircle className="h-4 w-4" />
                    <AlertDescription className="text-blue-200  text-sm">
                      <strong>Tip:</strong> You can use subdomains (like
                      app.mydomain.com) or root domains (like mydomain.com).
                      Make sure you own the domain and can modify its DNS
                      settings.
                    </AlertDescription>
                  </Alert>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Domains List */}
          {deploy.customDomain && (
            <Card className="holo-card">
              <CardHeader>
                <CardTitle className="text-cyan-400 ">Custom Domain</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700/50">
                        <TableHead className="text-slate-400 ">
                          Domain
                        </TableHead>
                        <TableHead className="text-slate-400 ">
                          DNS Status
                        </TableHead>
                        <TableHead className="text-slate-400 ">
                          SSL Status
                        </TableHead>
                        <TableHead className="text-slate-400 ">Added</TableHead>
                        <TableHead className="text-slate-400 ">
                          Actions
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {domainQuery.data && deploy.customDomain && (
                        <TableRow
                          key={domainQuery.data.id}
                          className="border-slate-700/50"
                        >
                          <TableCell className="font-medium text-cyan-100 ">
                            <div className="flex items-center gap-2">
                              {domainQuery.data.name}
                              {domainQuery.data.verified &&
                                domainQuery.data.sslStatus ===
                                  DOMAIN_STATUS.ACTIVE && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                      window.open(
                                        `https://${domainQuery.data.name}`,
                                        "_blank",
                                        "noopener,noreferrer",
                                      )
                                    }
                                    className="text-cyan-400 hover:text-cyan-300 p-1"
                                  >
                                    <ExternalLink className="h-3 w-3" />
                                  </Button>
                                )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {getDNSStatusBadge(domainQuery.data.verified)}
                          </TableCell>
                          <TableCell>
                            {getSSLStatusBadge(domainQuery.data.sslStatus)}
                          </TableCell>
                          <TableCell className="text-slate-300 ">
                            {new Date(
                              domainQuery.data.createdAt,
                            ).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeDomain()}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            >
                              {domainRemoveLoading ? (
                                <Spinner />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </Button>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
                {!domainQuery.data ||
                  (!deploy.customDomain && (
                    <div className="text-center py-8 text-slate-400 ">
                      No custom domains configured yet
                    </div>
                  ))}
              </CardContent>
            </Card>
          )}

          {/* DNS Configuration Instructions */}
          {deploy.customDomain && (
            <Card className="holo-card border-orange-500/30">
              <CardHeader>
                <CardTitle className="text-orange-400 ">
                  DNS Configuration Required
                </CardTitle>
                <CardDescription className="text-slate-400 ">
                  Configure these DNS records with your domain provider to
                  activate your custom domains
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {!domainQuery.data?.verified && (
                    <div
                      key={domainQuery?.data?.id}
                      className="p-4 bg-slate-800/30  border-orange-500/30 rounded-lg"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-medium text-cyan-100  text-lg">
                          {domainQuery?.data?.name}
                        </h4>
                        <Badge className="text-orange-400 bg-orange-400/10 border-orange-400/30 ">
                          WAITING FOR DNS
                        </Badge>
                      </div>

                      <div className="space-y-4">
                        <Alert className="border-blue-500/30 bg-blue-500/10">
                          <HelpCircle className="h-4 w-4" />
                          <AlertDescription className="text-blue-200  text-sm">
                            <strong>Instructions:</strong> Add this CNAME record
                            to your domain's DNS settings. This is usually done
                            through your domain registrar's control panel
                            (GoDaddy, Namecheap, Cloudflare, etc.).
                          </AlertDescription>
                        </Alert>

                        <div className="bg-slate-900/50 rounded-lg p-4 border border-cyan-500/30">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm ">
                            <div>
                              <span className="text-slate-400 block mb-1">
                                Record Type:
                              </span>
                              <span className="text-cyan-100 bg-slate-800/50 px-2 py-1 rounded">
                                CNAME
                              </span>
                            </div>
                            <div>
                              <span className="text-slate-400 block mb-1">
                                Name/Host:
                              </span>
                              <div className="flex items-center gap-2">
                                <span className="text-cyan-100 bg-slate-800/50 px-2 py-1 rounded">
                                  {domainQuery?.data?.name}
                                </span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    //@ts-ignore
                                    copyToClipboard(domainQuery?.data?.name)
                                  }
                                  className="text-slate-400 hover:text-cyan-400 p-1"
                                >
                                  <Copy className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                            <div>
                              <span className="text-slate-400 block mb-1">
                                Value/Target:
                              </span>
                              <div className="flex items-center gap-2">
                                <span className="text-cyan-100 bg-slate-800/50 px-2 py-1 rounded">
                                  {getDeployUrl()}
                                </span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    copyToClipboard(
                                      domainQuery.data?.internalSubdomain!,
                                    )
                                  }
                                  className="text-slate-400 hover:text-cyan-400 p-1"
                                >
                                  <Copy className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="text-xs text-slate-400 ">
                          <p>
                            • DNS changes can take up to 24 hours to propagate,
                            but usually happen within a few minutes
                          </p>
                          <p>
                            • We automatically check every 5 seconds and will
                            notify you when the domain is verified
                          </p>
                          <p>
                            • Once verified, we'll automatically generate an SSL
                            certificate for secure HTTPS access
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
